'use client';

import React, { useMemo } from 'react';
import { X, Route, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FunnelEdge, FunnelNode } from '../types';
import { trpc } from '@/lib/api/trpc';
import { useTranslation } from 'react-i18next';
import { ConditionBuilder } from './ConditionBuilder';
import { getQuickConditionsForSource } from '../constants/edgeConditions';

interface EdgeEditorPanelProps {
  edge: FunnelEdge;
  sourceNode: FunnelNode;
  targetNode: FunnelNode;
  allNodes: FunnelNode[];
  storeId: string;
  onChange: (edge: FunnelEdge) => void;
  onDelete: () => void;
  onClose: () => void;
  readOnly?: boolean;
}

export function EdgeEditorPanel({
  edge,
  sourceNode,
  targetNode,
  allNodes,
  storeId,
  onChange,
  onDelete,
  onClose,
  readOnly = false,
}: EdgeEditorPanelProps) {
  const { t } = useTranslation('storefront');
  const { data: availableConditions, isLoading: isLoadingConditions } =
    trpc.funnels.getAvailableConditions.useQuery(undefined, {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const quickConditions = useMemo(
    () => getQuickConditionsForSource(sourceNode.type),
    [sourceNode.type],
  );

  const setCondition = (key: string) => {
    if (readOnly) return;
    const currentWhen = edge.conditions?.when;
    const isActive = typeof currentWhen === 'string' && currentWhen === key;
    onChange({
      ...edge,
      conditions: {
        ...edge.conditions,
        when: isActive ? undefined : key,
      },
    });
  };

  const handlePriorityChange = (value: string) => {
    if (readOnly) return;
    const priority = value === '' ? undefined : parseInt(value) || 0;
    onChange({
      ...edge,
      conditions: { ...edge.conditions, priority },
    });
  };

  const currentWhen = edge.conditions?.when;

  return (
    <div className="flex h-full flex-col border-l bg-white dark:border-slate-700 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-600 dark:bg-violet-500">
            <Route className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {readOnly ? t('edgeEditor.connectionDetails') : t('edgeEditor.routingCondition')}
            </h2>
            <p className="text-xs text-gray-600 dark:text-slate-400">
              {sourceNode.name} → {targetNode.name}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Context-aware quick conditions based on source step type */}
        {!readOnly && quickConditions.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {t('edgeEditor.after', { name: sourceNode.name || sourceNode.type })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {quickConditions.map((qc) => {
                const isActive = typeof currentWhen === 'string' && currentWhen === qc.key;
                return (
                  <button
                    key={qc.key}
                    onClick={() => setCondition(qc.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? 'border-primary/40 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/10 dark:text-primary'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="mr-1">{qc.icon}</span>
                    {qc.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Info bar for unconditioned edges */}
        {!currentWhen && (
          <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800">
            <Info className="h-4 w-4 flex-shrink-0 text-slate-600 dark:text-slate-400" />
            <p className="text-sm text-slate-700 dark:text-slate-300">
              <strong>{t('edgeEditor.defaultRoute')}</strong> — {t('edgeEditor.takenUnconditionally')}
            </p>
          </div>
        )}

        {/* Condition Builder (advanced) */}
        {availableConditions ? (
          <div className={readOnly ? 'pointer-events-none opacity-80' : ''}>
            <ConditionBuilder
              availableConditions={availableConditions}
              value={currentWhen}
              onChange={(newWhen) => {
                if (readOnly) return;
                onChange({
                  ...edge,
                  conditions: { ...edge.conditions, when: newWhen },
                });
              }}
              storeId={storeId}
              sourceNodeType={sourceNode.type}
            />
          </div>
        ) : isLoadingConditions ? (
          <div className="text-center text-sm text-slate-500">{t('edgeEditor.loadingConditions')}</div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="border-t dark:border-slate-700">
        <div className="border-b px-4 py-3 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <Label htmlFor="edge-priority" className="text-xs font-medium">
              {t('edgeEditor.priority')}
            </Label>
            <Input
              id="edge-priority"
              type="number"
              min="0"
              value={edge.conditions?.priority ?? ''}
              onChange={(e) => handlePriorityChange(e.target.value)}
              placeholder="0"
              className="h-8 w-20"
              disabled={readOnly}
            />
            <span className="text-xs text-gray-500 dark:text-slate-400">{t('edgeEditor.higherCheckedFirst')}</span>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2">
            {!readOnly && (
              <Button variant="destructive" onClick={onDelete} className="flex-1">
                {t('edgeEditor.deleteConnection')}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} className="flex-1">
              {readOnly ? t('edgeEditor.close') : t('edgeEditor.done')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
